import { Head, Link } from "@inertiajs/react"
import { Ellipsis } from "lucide-react"

import StripeConnectCard from "@/components/partner/stripe-connect-card"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import PartnerSettingsLayout from "@/layouts/partner/settings-layout"
import PartnerLayout from "@/layouts/partner-layout"
import { partnerBillingPeriodsPath } from "@/routes"

interface Invoice {
  uid: string
  period_label: string
  billing_period_uid: string | null
  due_date: string
  paid_date: string | null
  amount_cents: number
  state: string
}

function fmt(cents: number) {
  return "$" + Math.round(cents / 100).toLocaleString()
}

export default function MarkupSettings({
  partner,
  invoices,
}: {
  partner: {
    uid: string
    default_markup_percentage: number
    billing_type: "imperative_managed" | "partner_managed"
    stripe_connected: boolean
    stripe_payouts_ready: boolean
    stripe_requirements: string[]
  }
  invoices: Invoice[]
}) {
  return (
    <PartnerLayout>
      <Head title="Settings — Payments" />
      <PartnerSettingsLayout>
        <div className="flex flex-col gap-8">
          {/* Markup configuration moved to the Pricing tab (IMP-281). */}

          {/* Payouts section (IMP-194). Only rendered for
              imperative-managed partners; partner-managed gets a
              Billing section in a later ticket. */}
          {partner.billing_type === "imperative_managed" && (
            <StripeConnectCard
              connected={partner.stripe_connected}
              payoutsReady={partner.stripe_payouts_ready}
              requirements={partner.stripe_requirements}
            />
          )}

          {/* Invoices section */}
          <div>
            <div className="mb-3">
              <h2 className="text-sm font-semibold">Invoices</h2>
            </div>
            {invoices.length === 0 ? (
              <p className="text-muted-foreground py-6 text-sm">
                No invoices yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="text-foreground min-w-full text-left text-sm/6">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="border-b px-4 py-2 font-medium first:pl-1">
                        Period
                      </th>
                      <th className="border-b px-4 py-2 font-medium">Due</th>
                      <th className="border-b px-4 py-2 font-medium">Paid</th>
                      <th className="border-b px-4 py-2 text-right font-medium">
                        Amount
                      </th>
                      <th className="border-b px-4 py-2 text-right font-medium">
                        Status
                      </th>
                      <th className="border-b px-4 py-2 pr-1 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.uid} className="hover:bg-muted/50">
                        <td className="border-b px-4 py-4 font-medium first:pl-1">
                          {inv.period_label}
                        </td>
                        <td className="text-muted-foreground border-b px-4 py-4">
                          {inv.due_date}
                        </td>
                        <td className="text-muted-foreground border-b px-4 py-4">
                          {inv.paid_date ?? (
                            <span className="text-muted-foreground/40">—</span>
                          )}
                        </td>
                        <td className="border-b px-4 py-4 text-right font-medium tabular-nums">
                          {fmt(inv.amount_cents)}
                        </td>
                        <td className="border-b px-4 py-4 text-right">
                          <Badge
                            variant={
                              inv.state === "paid" ? "operational" : "warning"
                            }
                          >
                            {inv.state === "paid" ? "Paid" : "Outstanding"}
                          </Badge>
                        </td>
                        <td className="border-b px-4 py-4 pr-1 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="text-muted-foreground hover:text-foreground p-1">
                                <Ellipsis className="size-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link
                                  href={partnerBillingPeriodsPath({
                                    period: inv.billing_period_uid ?? undefined,
                                  })}
                                >
                                  View statement
                                </Link>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </PartnerSettingsLayout>
    </PartnerLayout>
  )
}
